import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { writeAudit } from "@/lib/intel/audit.server";

export type Severity = "low" | "medium" | "high" | "critical";

// ============================================================
// 1. Scan history (Supabase)
// ============================================================
export type ScanRunSummary = {
  id: string;
  target: string;
  targetType: string;
  status: string;
  riskScore: number;
  confidence: number;
  sourceCount: number;
  createdAt: string;
  completedAt: string | null;
  workspace: string | null;
};

export const listScanRuns = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ScanRunSummary[]> => {
  const db = supabaseAdmin as any;
  let q = db
    .from("intel_scan_runs")
    .select("id, target, target_type, status, risk_score, confidence, source_count, created_at, completed_at, workspace")
    .order("created_at", { ascending: false })
    .limit(100);
  // Admins see everything; regular users see only their own scans.
  const { data: isAdmin } = await db.rpc("has_role", { _user_id: context.userId, _role: "admin" });
  if (!isAdmin) q = q.eq("user_id", context.userId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []).map((r: any) => ({
    id: r.id,
    target: r.target,
    targetType: r.target_type,
    status: r.status,
    riskScore: r.risk_score ?? 0,
    confidence: r.confidence ?? 0,
    sourceCount: r.source_count ?? 0,
    createdAt: r.created_at,
    completedAt: r.completed_at,
    workspace: r.workspace ?? null,
  }));
});

export const getScanRun = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const db = supabaseAdmin as any;
    const { data: row, error } = await db
      .from("intel_scan_runs")
      .select("*")
      .eq("id", data.id)
      .single();
    if (error) throw new Error(error.message);
    const { data: isAdmin } = await db.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin && row?.user_id && row.user_id !== context.userId) {
      throw new Error("Forbidden: scan belongs to another user");
    }
    void writeAudit({
      scanRunId: data.id,
      userId: context.userId,
      actorEmail: (context.claims as any)?.email ?? null,
      action: "view",
      metadata: { required_clearance: row?.required_clearance ?? "L1" },
    });
    return row;
  });

export const deleteScanRun = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const db = supabaseAdmin as any;
    const { data: row, error: getErr } = await db
      .from("intel_scan_runs")
      .select("user_id")
      .eq("id", data.id)
      .single();
    if (getErr) throw new Error(getErr.message);
    const { data: isAdmin } = await db.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin && row?.user_id && row.user_id !== context.userId) {
      throw new Error("Forbidden: scan belongs to another user");
    }
    const { error } = await db.from("intel_scan_runs").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    void writeAudit({
      scanRunId: data.id,
      userId: context.userId,
      actorEmail: (context.claims as any)?.email ?? null,
      action: "delete_scan",
    });
    return { ok: true };
  });

// ------- Scan notes (per-scan analyst notes) -------
export type ScanNote = {
  id: string;
  scanRunId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
};

async function assertScanAccess(db: any, scanId: string, userId: string) {
  const { data: row, error } = await db
    .from("intel_scan_runs")
    .select("user_id")
    .eq("id", scanId)
    .single();
  if (error) throw new Error(error.message);
  const { data: isAdmin } = await db.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!isAdmin && row?.user_id && row.user_id !== userId) {
    throw new Error("Forbidden: scan belongs to another user");
  }
}

export const listScanNotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ scanRunId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<ScanNote[]> => {
    const db = supabaseAdmin as any;
    await assertScanAccess(db, data.scanRunId, context.userId);
    const { data: rows, error } = await db
      .from("scan_notes")
      .select("id, scan_run_id, body, created_at, updated_at")
      .eq("scan_run_id", data.scanRunId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r: any) => ({
      id: r.id,
      scanRunId: r.scan_run_id,
      body: r.body,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  });

export const addScanNote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ scanRunId: z.string().uuid(), body: z.string().trim().min(1).max(4000) }).parse(d),
  )
  .handler(async ({ data, context }): Promise<ScanNote> => {
    const db = supabaseAdmin as any;
    await assertScanAccess(db, data.scanRunId, context.userId);
    const { data: row, error } = await db
      .from("scan_notes")
      .insert({ scan_run_id: data.scanRunId, user_id: context.userId, body: data.body })
      .select("id, scan_run_id, body, created_at, updated_at")
      .single();
    if (error) throw new Error(error.message);
    void writeAudit({
      scanRunId: data.scanRunId,
      userId: context.userId,
      actorEmail: (context.claims as any)?.email ?? null,
      action: "note_add",
      metadata: { note_id: row.id },
    });
    return {
      id: row.id,
      scanRunId: row.scan_run_id,
      body: row.body,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  });

export const deleteScanNote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const db = supabaseAdmin as any;
    const { data: row, error: getErr } = await db
      .from("scan_notes")
      .select("user_id, scan_run_id")
      .eq("id", data.id)
      .single();
    if (getErr) throw new Error(getErr.message);
    const { data: isAdmin } = await db.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin && row?.user_id !== context.userId) {
      throw new Error("Forbidden: note belongs to another user");
    }
    const { error } = await db.from("scan_notes").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    if (row?.scan_run_id) {
      void writeAudit({
        scanRunId: row.scan_run_id,
        userId: context.userId,
        actorEmail: (context.claims as any)?.email ?? null,
        action: "note_delete",
        metadata: { note_id: data.id },
      });
    }
    return { ok: true };
  });

// ============================================================
// 1c. Per-scan AI chat (persisted)
// ============================================================
export type ScanChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations: string[];
  createdAt: string;
};

export const listScanChat = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ scanRunId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<ScanChatMessage[]> => {
    const db = supabaseAdmin as any;
    await assertScanAccess(db, data.scanRunId, context.userId);
    const { data: rows, error } = await db
      .from("scan_chats")
      .select("id, role, content, citations, created_at")
      .eq("scan_run_id", data.scanRunId)
      .neq("role", "system")
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r: any) => ({
      id: r.id,
      role: r.role,
      content: r.content,
      citations: Array.isArray(r.citations) ? r.citations : [],
      createdAt: r.created_at,
    }));
  });

export const clearScanChat = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ scanRunId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const db = supabaseAdmin as any;
    await assertScanAccess(db, data.scanRunId, context.userId);
    const { error } = await db.from("scan_chats").delete().eq("scan_run_id", data.scanRunId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const sendScanChat = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      scanRunId: z.string().uuid(),
      message: z.string().trim().min(1).max(4000),
      environment: z.enum(["sandbox", "live"]).default("live"),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<{ assistant: ScanChatMessage }> => {
    const db = supabaseAdmin as any;
    await assertScanAccess(db, data.scanRunId, context.userId);

    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({
      supabase: context.supabase,
      userId: context.userId,
      feature: "ai",
      environment: data.environment,
    });

    // Persist the user turn first.
    await db.from("scan_chats").insert({
      scan_run_id: data.scanRunId,
      user_id: context.userId,
      role: "user",
      content: data.message,
    });

    // Load full thread + scan context.
    const { data: thread } = await db
      .from("scan_chats")
      .select("role, content")
      .eq("scan_run_id", data.scanRunId)
      .neq("role", "system")
      .order("created_at", { ascending: true })
      .limit(40);

    const { data: run } = await db
      .from("intel_scan_runs")
      .select("target, target_type, risk_score, confidence, source_count, findings, entities, status, completed_at")
      .eq("id", data.scanRunId)
      .single();

    const findingsBlock = (run?.findings ?? []).slice(0, 12).map(
      (f: any) => `- [${f.severity}] ${f.title} (${f.source}): ${f.description}`,
    ).join("\n");
    const entitiesBlock = (run?.entities ?? []).slice(0, 12).map(
      (e: any) => `- ${e.type}: ${e.label ?? e.value}`,
    ).join("\n");

    const systemMsg = {
      role: "system" as const,
      content:
        "You are CARIO-AGENT, the OSINT analyst copilot focused on a SINGLE saved scan. STRICT POLICY: only LEGAL, publicly accessible OSINT. Use Live Search to enrich and cite sources. Use Markdown.\n\n" +
        `ACTIVE SCAN CONTEXT\nTarget: ${run?.target} (${run?.target_type})\nStatus: ${run?.status}\nRisk: ${run?.risk_score ?? 0}/100  Confidence: ${run?.confidence ?? 0}%\nCollectors executed: ${run?.source_count ?? 0}\n\nTOP FINDINGS:\n${findingsBlock || "(none)"}\n\nTOP ENTITIES:\n${entitiesBlock || "(none)"}\n\n` +
        "When the user asks to 'broaden the search', 'widen', 'go deeper', or similar, propose 3-6 concrete new collectors / pivots / search angles. Tell them they can click the 'Broaden search' button to re-run the scan without refine filters.\n\n" +
        "COPILOT BEHAVIOUR (always): do not merely answer — investigate. (1) Answer the question with live-search verification. (2) Attach a calibrated Confidence: X% to every conclusion, derived from corroboration and source authority, and say what would raise it. (3) End every reply with a short '**Next pivots**' list of 2-4 concrete pivots, each with why it matters. (4) Call out dead ends explicitly when an avenue is exhausted. (5) State hypotheses as hypotheses, e.g. 'This email is likely connected to X, Y and Z because…'.",
    };

    const { callGrok } = await import("@/lib/intel/grok.server");
    const res = await callGrok(
      [systemMsg, ...(thread ?? []).map((m: any) => ({ role: m.role as "user" | "assistant", content: m.content }))],
      { liveSearch: true },
    );
    let reply = res.content || "(no response)";
    if (res.citations.length) {
      reply += "\n\n**Sources**\n" + res.citations.slice(0, 12).map((c, i) => `${i + 1}. ${c}`).join("\n");
    }

    const { data: saved, error: saveErr } = await db
      .from("scan_chats")
      .insert({
        scan_run_id: data.scanRunId,
        user_id: context.userId,
        role: "assistant",
        content: reply,
        citations: res.citations ?? [],
      })
      .select("id, role, content, citations, created_at")
      .single();
    if (saveErr) throw new Error(saveErr.message);

    void writeAudit({
      scanRunId: data.scanRunId,
      userId: context.userId,
      actorEmail: (context.claims as any)?.email ?? null,
      action: "contradiction_scan",
      metadata: { prompt_len: data.message.length, reply_len: reply.length },
    });

    return {
      assistant: {
        id: saved.id,
        role: "assistant",
        content: saved.content,
        citations: Array.isArray(saved.citations) ? saved.citations : [],
        createdAt: saved.created_at,
      },
    };
  });

// ============================================================
// 1c-2. AI Investigation Copilot (proactive investigation plan)
// ============================================================
export type CopilotPivot = {
  target: string;
  type: string;
  why: string;
  confidence: number;
  priority: "low" | "medium" | "high";
  query: string;
};
export type CopilotHypothesis = {
  statement: string;
  reasoning: string;
  confidence: number;
  supporting: string[];
  contradicting: string[];
  tests: string[];
};
export type CopilotConclusion = { statement: string; confidence: number; basis: string };
export type CopilotDeadEnd = { path: string; reason: string };
export type CopilotTraceStep = {
  step: number;
  action: string;
  observation: string;
  inference: string;
  evidence: string[];
  confidence: number;
  outcome: "linked" | "corroborated" | "weak" | "dead_end";
  pivot?: string;
};
export type CopilotPlan = {
  summary: string;
  overallConfidence: number;
  reasoningTrace: CopilotTraceStep[];
  hypotheses: CopilotHypothesis[];
  pivots: CopilotPivot[];
  deadEnds: CopilotDeadEnd[];
  conclusions: CopilotConclusion[];
  citations: string[];
  generatedAt: string;
};

function clampPct(n: unknown, fallback = 50): number {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(0, Math.min(100, Math.round(v)));
}

export const runInvestigationCopilot = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      scanRunId: z.string().uuid(),
      environment: z.enum(["sandbox", "live"]).default("live"),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<CopilotPlan> => {
    const db = supabaseAdmin as any;
    await assertScanAccess(db, data.scanRunId, context.userId);

    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({
      supabase: context.supabase,
      userId: context.userId,
      feature: "ai",
      environment: data.environment,
    });

    const { data: run } = await db
      .from("intel_scan_runs")
      .select("target, target_type, risk_score, confidence, source_count, findings, entities, collectors, timeline, status")
      .eq("id", data.scanRunId)
      .single();

    const findingsBlock = (run?.findings ?? []).slice(0, 25).map(
      (f: any) => `- [${f.severity}] ${f.title} (${f.source}): ${f.description}`,
    ).join("\n");
    const entitiesBlock = (run?.entities ?? []).slice(0, 25).map(
      (e: any) => `- ${e.type}: ${e.label ?? e.value}${e.confidence ? ` (conf ${e.confidence})` : ""}`,
    ).join("\n");
    const collectorsBlock = Array.isArray(run?.collectors)
      ? run.collectors.slice(0, 40).map((c: any) => `- ${c.source}: ${c.status}${c.summary ? ` — ${String(c.summary).slice(0, 140)}` : ""}`).join("\n")
      : "";
    const timelineBlock = Array.isArray(run?.timeline)
      ? [...run.timeline]
          .filter((t: any) => t?.kind !== "run" && t?.category !== "run")
          .sort((a: any, b: any) => String(a?.ts).localeCompare(String(b?.ts)))
          .slice(0, 40)
          .map((t: any) => `- ${String(t?.ts ?? "").slice(0, 10)} [${t?.category ?? "evidence"}] ${t?.title} (${t?.source})`)
          .join("\n")
      : "";

    const system = {
      role: "system" as const,
      content: [
        "You are CARIO-COPILOT, a proactive OSINT investigation lead (not a passive Q&A bot).",
        "STRICT POLICY: only LEGAL, publicly accessible OSINT. Never speculate without labelling it as a hypothesis.",
        "Use live web/X search to verify and enrich, then plan the NEXT moves of the investigation.",
        "Every hypothesis and conclusion MUST carry a calibrated confidence 0-100 derived from evidence quality, corroboration count and source authority — never a sequential or arbitrary number.",
        "Identify DEAD ENDS: avenues already exhausted, blocked, or with no realistic public evidence, so the analyst does not waste time.",
        "Show your work: emit an ordered reasoning_trace where each step names the observation it started from, the inference drawn, the evidence relied on, and the outcome (linked | corroborated | weak | dead_end). Every dead end you report must be traceable to a trace step.",
        "Use the reconstructed chronology to reason about SEQUENCE (what happened first, what predates what).",
        "Return STRICT JSON only. No prose outside JSON.",
      ].join(" "),
    };
    const user = {
      role: "user" as const,
      content: `TARGET: ${run?.target} (${run?.target_type})
STATUS: ${run?.status} · trust/risk ${run?.risk_score ?? 0}/100 · confidence ${run?.confidence ?? 0}% · ${run?.source_count ?? 0} collectors

FINDINGS:
${findingsBlock || "(none)"}

ENTITIES:
${entitiesBlock || "(none)"}

COLLECTOR RESULTS:
${collectorsBlock || "(none)"}

RECONSTRUCTED CHRONOLOGY (oldest first):
${timelineBlock || "(no dated evidence)"}

TASK: actively investigate. Verify with live search, then return JSON:
{
  "summary": "2-4 sentences: what we know, what is still unresolved",
  "overall_confidence": 0-100,
  "reasoning_trace": [{"action":"short label e.g. Linked email to domain registrant","observation":"the finding/entity/collector this step started from","inference":"what follows and why","evidence":["source name or url"],"confidence":0-100,"outcome":"linked|corroborated|weak|dead_end","pivot":"optional selector this step produced"}],
  "hypotheses": [{"statement":"e.g. This email is likely connected to Acme Ltd, Beta Oy and Gamma AB","reasoning":"because ...","confidence":0-100,"supporting":["evidence or url"],"contradicting":["..."],"tests":["concrete check that would confirm/refute"]}],
  "pivots": [{"target":"the exact selector to pivot on","type":"domain|email|username|wallet|ip|phone|person|organization|document","why":"why this pivot matters to the case","confidence":0-100,"priority":"low|medium|high","query":"the exact question to ask the analyst copilot next"}],
  "dead_ends": [{"path":"avenue tried","reason":"why it is exhausted"}],
  "conclusions": [{"statement":"defensible conclusion","confidence":0-100,"basis":"evidence it rests on"}]
}
Give 4-10 ordered reasoning_trace steps, 3-6 pivots, 2-4 hypotheses, 0-4 dead ends, 1-4 conclusions.`,
    };

    const { callGrok } = await import("@/lib/intel/grok.server");
    const res = await callGrok([system, user], { liveSearch: true });
    let parsed: any = {};
    try { parsed = JSON.parse(res.content); } catch {
      const m = res.content.match(/\{[\s\S]*\}/);
      if (m) { try { parsed = JSON.parse(m[0]); } catch { /* ignore */ } }
    }

    const asArr = (v: unknown) => (Array.isArray(v) ? v : []);
    const plan: CopilotPlan = {
      summary: typeof parsed.summary === "string" ? parsed.summary : "No synthesis could be generated from the current evidence.",
      overallConfidence: clampPct(parsed.overall_confidence, run?.confidence ?? 40),
      reasoningTrace: asArr(parsed.reasoning_trace).slice(0, 12).map((s: any, i: number) => ({
        step: i + 1,
        action: String(s?.action ?? "").slice(0, 160),
        observation: String(s?.observation ?? "").slice(0, 600),
        inference: String(s?.inference ?? "").slice(0, 900),
        evidence: asArr(s?.evidence).map((e: any) => String(e).slice(0, 300)).slice(0, 8),
        confidence: clampPct(s?.confidence, 50),
        outcome: (["linked", "corroborated", "weak", "dead_end"].includes(s?.outcome) ? s.outcome : "linked") as CopilotTraceStep["outcome"],
        ...(s?.pivot ? { pivot: String(s.pivot).slice(0, 200) } : {}),
      })).filter((s) => s.action || s.inference),
      hypotheses: asArr(parsed.hypotheses).slice(0, 6).map((h: any) => ({
        statement: String(h?.statement ?? "").slice(0, 500),
        reasoning: String(h?.reasoning ?? "").slice(0, 1200),
        confidence: clampPct(h?.confidence, 40),
        supporting: asArr(h?.supporting).map((s: any) => String(s)).slice(0, 8),
        contradicting: asArr(h?.contradicting).map((s: any) => String(s)).slice(0, 8),
        tests: asArr(h?.tests).map((s: any) => String(s)).slice(0, 6),
      })).filter((h) => h.statement),
      pivots: asArr(parsed.pivots).slice(0, 8).map((p: any) => ({
        target: String(p?.target ?? "").slice(0, 200),
        type: String(p?.type ?? "unknown"),
        why: String(p?.why ?? "").slice(0, 600),
        confidence: clampPct(p?.confidence, 50),
        priority: (["low", "medium", "high"].includes(p?.priority) ? p.priority : "medium") as CopilotPivot["priority"],
        query: String(p?.query ?? `Investigate ${p?.target ?? ""} and explain its link to ${run?.target}`).slice(0, 400),
      })).filter((p) => p.target),
      deadEnds: asArr(parsed.dead_ends).slice(0, 6).map((d: any) => ({
        path: String(d?.path ?? "").slice(0, 200),
        reason: String(d?.reason ?? "").slice(0, 400),
      })).filter((d) => d.path),
      conclusions: asArr(parsed.conclusions).slice(0, 6).map((c: any) => ({
        statement: String(c?.statement ?? "").slice(0, 500),
        confidence: clampPct(c?.confidence, 40),
        basis: String(c?.basis ?? "").slice(0, 600),
      })).filter((c) => c.statement),
      citations: (res.citations ?? []).slice(0, 20),
      generatedAt: new Date().toISOString(),
    };

    void writeAudit({
      scanRunId: data.scanRunId,
      userId: context.userId,
      actorEmail: (context.claims as any)?.email ?? null,
      action: "chat",
      metadata: { copilot: true, pivots: plan.pivots.length, hypotheses: plan.hypotheses.length },
    });

    return plan;
  });

// ============================================================
// 1d. AI credit packs (purchased top-ups)
// ============================================================
export const getMyAiCredits = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ remaining: number; packs: number }> => {
    const db = supabaseAdmin as any;
    const { data: rows } = await db
      .from("ai_credit_packs")
      .select("credits_remaining")
      .eq("user_id", context.userId)
      .gt("credits_remaining", 0);
    const remaining = (rows ?? []).reduce((a: number, r: any) => a + (r.credits_remaining ?? 0), 0);
    return { remaining, packs: rows?.length ?? 0 };
  });

// ============================================================
// 2. Threat feed (CISA KEV + NVD)
// ============================================================
export type ThreatItem = {
  id: string;
  source: "CISA KEV" | "NVD";
  cve: string;
  title: string;
  description: string;
  severity: Severity;
  cvss: number | null;
  vendor: string;
  product: string;
  publishedAt: string;
  ref: string;
};

function cvssToSeverity(score: number | null): Severity {
  if (score == null) return "medium";
  if (score >= 9) return "critical";
  if (score >= 7) return "high";
  if (score >= 4) return "medium";
  return "low";
}

export const fetchThreatFeed = createServerFn({ method: "GET" })
  .handler(async (): Promise<ThreatItem[]> => {
  const items: ThreatItem[] = [];

  // CISA Known Exploited Vulnerabilities
  try {
    const r = await fetch("https://www.cisa.gov/sites/default/files/feeds/known_exploited_vulnerabilities.json");
    if (r.ok) {
      const data = (await r.json()) as { vulnerabilities?: any[] };
      for (const v of (data.vulnerabilities ?? []).slice(-25).reverse()) {
        items.push({
          id: `kev-${v.cveID}`,
          source: "CISA KEV",
          cve: v.cveID,
          title: v.vulnerabilityName ?? v.cveID,
          description: v.shortDescription ?? "",
          severity: "critical",
          cvss: null,
          vendor: v.vendorProject ?? "",
          product: v.product ?? "",
          publishedAt: v.dateAdded ?? "",
          ref: `https://nvd.nist.gov/vuln/detail/${v.cveID}`,
        });
      }
    }
  } catch { /* swallow */ }

  // NVD recent CVEs (last 7 days)
  try {
    const since = new Date(Date.now() - 7 * 86400000).toISOString();
    const until = new Date().toISOString();
    const r = await fetch(
      `https://services.nvd.nist.gov/rest/json/cves/2.0?pubStartDate=${encodeURIComponent(since)}&pubEndDate=${encodeURIComponent(until)}&resultsPerPage=30`,
      { headers: { "user-agent": "CARIO-INTEL" } },
    );
    if (r.ok) {
      const data = (await r.json()) as any;
      for (const entry of (data.vulnerabilities ?? []).slice(0, 30)) {
        const cve = entry.cve;
        const metric =
          cve.metrics?.cvssMetricV31?.[0]?.cvssData ??
          cve.metrics?.cvssMetricV30?.[0]?.cvssData ??
          cve.metrics?.cvssMetricV2?.[0]?.cvssData;
        const score = metric?.baseScore ?? null;
        const desc = cve.descriptions?.find((d: any) => d.lang === "en")?.value ?? "";
        items.push({
          id: `nvd-${cve.id}`,
          source: "NVD",
          cve: cve.id,
          title: cve.id,
          description: desc.slice(0, 280),
          severity: cvssToSeverity(score),
          cvss: score,
          vendor: cve.configurations?.[0]?.nodes?.[0]?.cpeMatch?.[0]?.criteria?.split(":")[3] ?? "",
          product: cve.configurations?.[0]?.nodes?.[0]?.cpeMatch?.[0]?.criteria?.split(":")[4] ?? "",
          publishedAt: cve.published ?? "",
          ref: `https://nvd.nist.gov/vuln/detail/${cve.id}`,
        });
      }
    }
  } catch { /* swallow */ }

  return items;
});

// ============================================================
// 3. Live events (HN + Wikipedia + GitHub public)
// ============================================================
export type LiveEvent = {
  id: string;
  source: "HackerNews" | "Wikipedia" | "GitHub";
  title: string;
  ts: string;
  url: string;
  meta: string;
  severity: Severity;
};

export const fetchLiveEvents = createServerFn({ method: "GET" }).handler(async (): Promise<LiveEvent[]> => {
  const out: LiveEvent[] = [];

  const t = (ms: number) => AbortSignal.timeout(ms);

  // Run all 3 upstream feeds in parallel instead of serial waterfalls.
  const hnTask = (async () => {
    const ids = (await (await fetch("https://hacker-news.firebaseio.com/v0/newstories.json")).json()) as number[];
    const top = ids.slice(0, 12);
    const stories = await Promise.all(top.map((id) => fetch(`https://hacker-news.firebaseio.com/v0/item/${id}.json`).then((r) => r.json()).catch(() => null)));
    for (const s of stories) {
      if (!s) continue;
      out.push({
        id: `hn-${s.id}`,
        source: "HackerNews",
        title: s.title ?? "(untitled)",
        ts: new Date((s.time ?? 0) * 1000).toISOString(),
        url: s.url ?? `https://news.ycombinator.com/item?id=${s.id}`,
        meta: `${s.score ?? 0} pts · by ${s.by ?? "anon"}`,
        severity: "low",
      });
    }
  })().catch(() => {});

  const wikiTask = (async () => {
    const r = await fetch(
      "https://en.wikipedia.org/w/api.php?action=query&list=recentchanges&rcprop=title|timestamp|user|comment|ids&rclimit=15&format=json&origin=*",
      { signal: t(6000), headers: { "user-agent": "CARIO-INTEL" } },
    );
    if (r.ok) {
      const data = (await r.json()) as any;
      for (const c of data.query?.recentchanges ?? []) {
        out.push({
          id: `wiki-${c.rcid}`,
          source: "Wikipedia",
          title: c.title,
          ts: c.timestamp,
          url: `https://en.wikipedia.org/wiki/${encodeURIComponent(c.title)}`,
          meta: `edited by ${c.user}`,
          severity: "low",
        });
      }
    }
  })().catch(() => {});

  const ghTask = (async () => {
    const r = await fetch("https://api.github.com/events?per_page=15", {
      signal: t(6000),
      headers: { accept: "application/vnd.github+json", "user-agent": "CARIO-INTEL" },
    });
    if (r.ok) {
      const events = (await r.json()) as any[];
      for (const e of events) {
        out.push({
          id: `gh-${e.id}`,
          source: "GitHub",
          title: `${e.type} · ${e.repo?.name ?? ""}`,
          ts: e.created_at,
          url: `https://github.com/${e.repo?.name ?? ""}`,
          meta: `actor ${e.actor?.login ?? "anon"}`,
          severity: "low",
        });
      }
    }
  })().catch(() => {});

  await Promise.all([hnTask, wikiTask, ghTask]);

  return out.sort((a, b) => (b.ts > a.ts ? 1 : -1)).slice(0, 40);
});

// ============================================================
// 4. GEOINT (USGS earthquakes + ISS)
// ============================================================
export type GeoPoint = {
  id: string;
  name: string;
  type: string;
  lat: number;
  lon: number;
  risk: number;
  confidence: number;
  detail: string;
  ts: string;
};

export const fetchGeoIntel = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async (): Promise<GeoPoint[]> => {
  const points: GeoPoint[] = [];
  const t = (ms: number) => AbortSignal.timeout(ms);

  // USGS — M4.5+ earthquakes past week (richer than significant_week which often has <5 events)
  try {
    const r = await fetch(
      "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_week.geojson",
      { signal: t(8000), headers: { "user-agent": "CARIO-INTEL" } },
    );
    if (r.ok) {
      const data = (await r.json()) as any;
      for (const f of (data.features ?? []).slice(0, 40)) {
        const [lon, lat] = f.geometry?.coordinates ?? [0, 0];
        if (typeof lat !== "number" || typeof lon !== "number") continue;
        const mag = f.properties?.mag ?? 0;
        points.push({
          id: f.id,
          name: f.properties?.place ?? "Unknown",
          type: "seismic event",
          lat,
          lon,
          risk: Math.min(100, Math.max(5, Math.round(mag * 12))),
          confidence: 92,
          detail: `Magnitude ${mag} · ${f.properties?.type ?? "earthquake"} · USGS M4.5+ feed`,
          ts: new Date(f.properties?.time ?? Date.now()).toISOString(),
        });
      }
    }
  } catch { /* swallow */ }

  // NASA EONET — open natural events (wildfires, storms, volcanoes, ice)
  try {
    const r = await fetch(
      "https://eonet.gsfc.nasa.gov/api/v3/events?status=open&limit=40",
      { signal: t(8000), headers: { "user-agent": "CARIO-INTEL" } },
    );
    if (r.ok) {
      const data = (await r.json()) as any;
      for (const ev of (data.events ?? []) as any[]) {
        const geo = ev.geometry?.[ev.geometry.length - 1];
        if (!geo) continue;
        let lat: number | null = null;
        let lon: number | null = null;
        if (geo.type === "Point" && Array.isArray(geo.coordinates)) {
          [lon, lat] = geo.coordinates;
        } else if (geo.type === "Polygon" && Array.isArray(geo.coordinates?.[0])) {
          const ring = geo.coordinates[0] as number[][];
          lon = ring.reduce((s, p) => s + p[0], 0) / ring.length;
          lat = ring.reduce((s, p) => s + p[1], 0) / ring.length;
        }
        if (typeof lat !== "number" || typeof lon !== "number") continue;
        const cat = ev.categories?.[0]?.title ?? "event";
        const risk =
          /wildfire/i.test(cat) ? 70 :
          /volcano/i.test(cat) ? 80 :
          /storm|cyclone|hurricane/i.test(cat) ? 75 :
          /flood/i.test(cat) ? 65 :
          /ice|iceberg/i.test(cat) ? 30 : 45;
        points.push({
          id: `eonet-${ev.id}`,
          name: ev.title ?? cat,
          type: cat.toLowerCase(),
          lat,
          lon,
          risk,
          confidence: 88,
          detail: `${cat} · NASA EONET open event${ev.sources?.[0]?.id ? ` · src ${ev.sources[0].id}` : ""}`,
          ts: geo.date ?? new Date().toISOString(),
        });
      }
    }
  } catch { /* swallow */ }

  // ISS live ground track
  try {
    const r = await fetch("http://api.open-notify.org/iss-now.json", { signal: t(5000) });
    if (r.ok) {
      const data = (await r.json()) as any;
      const lat = parseFloat(data.iss_position?.latitude ?? "0");
      const lon = parseFloat(data.iss_position?.longitude ?? "0");
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
      points.push({
        id: "iss",
        name: "ISS Current Position",
        type: "orbital telemetry",
        lat,
        lon,
        risk: 5,
        confidence: 99,
        detail: "International Space Station live ground track",
        ts: new Date().toISOString(),
      });
      }
    }
  } catch { /* swallow */ }
  return points.sort((a, b) => b.risk - a.risk);
});

// ============================================================
// 5. Source health (HEAD ping public collectors)
// ============================================================
export type SourceHealth = {
  name: string;
  url: string;
  category: string;
  latency: number;
  status: "online" | "degraded" | "offline";
  httpStatus: number | null;
};

const SOURCE_REGISTRY: Array<{ name: string; url: string; category: string }> = [
  { name: "Cloudflare DNS-over-HTTPS", url: "https://cloudflare-dns.com/dns-query?name=example.com&type=A", category: "infrastructure" },
  { name: "RDAP (rdap.org)", url: "https://rdap.org/domain/example.com", category: "infrastructure" },
  { name: "Certificate Transparency (crt.sh)", url: "https://crt.sh/?q=example.com&output=json", category: "infrastructure" },
  { name: "GitHub public API", url: "https://api.github.com/rate_limit", category: "code" },
  { name: "Hacker News firehose", url: "https://hacker-news.firebaseio.com/v0/maxitem.json", category: "social" },
  { name: "Wikipedia API", url: "https://en.wikipedia.org/w/api.php?action=query&meta=siteinfo&format=json", category: "public web" },
  { name: "USGS earthquake feed", url: "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/significant_week.geojson", category: "geoint" },
  { name: "NASA EONET events", url: "https://eonet.gsfc.nasa.gov/api/v3/events?status=open&limit=1", category: "geoint" },
  { name: "CISA KEV catalog", url: "https://www.cisa.gov/sites/default/files/feeds/known_exploited_vulnerabilities.json", category: "compliance" },
  { name: "NVD CVE API", url: "https://services.nvd.nist.gov/rest/json/cves/2.0?resultsPerPage=1", category: "compliance" },
  { name: "Open-Notify ISS", url: "http://api.open-notify.org/iss-now.json", category: "geoint" },
];

export const fetchSourceHealth = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async (): Promise<SourceHealth[]> => {
  return Promise.all(
    SOURCE_REGISTRY.map(async (s) => {
      const start = Date.now();
      try {
        const r = await fetch(s.url, { method: "GET", headers: { "user-agent": "CARIO-INTEL" } });
        const latency = Date.now() - start;
        return {
          name: s.name,
          url: s.url,
          category: s.category,
          latency,
          status: r.ok ? (latency > 1500 ? "degraded" : "online") : "degraded",
          httpStatus: r.status,
        } as SourceHealth;
      } catch {
        return {
          name: s.name,
          url: s.url,
          category: s.category,
          latency: Date.now() - start,
          status: "offline",
          httpStatus: null,
        } as SourceHealth;
      }
    }),
  );
});

// ============================================================
// 6. AI Analyst via Lovable AI Gateway
// ============================================================
export const askAnalyst = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      messages: z
        .array(z.object({ role: z.enum(["user", "assistant"]), content: z.string().min(1).max(8000) }))
        .min(1)
        .max(40),
      environment: z.enum(["sandbox", "live"]).default("live"),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<{ reply: string }> => {
    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({ supabase: context.supabase, userId: context.userId, feature: "ai", environment: data.environment });
    // Pull last few scan runs to ground the AI
    let runContext = "";
    try {
      const db = supabaseAdmin as any;
      const { data: runs } = await db
        .from("intel_scan_runs")
        .select("target, target_type, risk_score, confidence, source_count, status, created_at")
        .eq("user_id", context.userId)
        .order("created_at", { ascending: false })
        .limit(3);
      if (runs?.length) {
        runContext = "Recent scan runs:\n" + runs.map((r: any) =>
          `- ${r.target} (${r.target_type}) risk=${r.risk_score} conf=${r.confidence} status=${r.status}`
        ).join("\n");
      }
    } catch { /* ignore */ }

    const systemMsg = {
      role: "system" as const,
      content:
        "You are GROK-AGENT, the lead OSINT analyst copilot for CARIO INTEL, powered by Grok with Live Search. STRICT POLICY: only LEGAL, publicly accessible OSINT. Refuse doxxing or illegal access. When asked about a specific target, USE LIVE SEARCH to verify and cite sources inline. Use Markdown.\n\n" +
        runContext,
    };

    const { callGrok } = await import("@/lib/intel/grok.server");
    const res = await callGrok([systemMsg, ...data.messages], { liveSearch: true });
    let reply = res.content || "(no response)";
    if (res.citations.length) {
      reply += "\n\n**Sources**\n" + res.citations.slice(0, 12).map((c, i) => `${i + 1}. ${c}`).join("\n");
    }
    return { reply };
  });

// ============================================================
// 7. AI-generated dossier summary
// ============================================================
export const generateDossier = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ runId: z.string().uuid(), environment: z.enum(["sandbox", "live"]).default("live") }).parse(d))
  .handler(async ({ data, context }): Promise<{ summary: string; recommendations: string }> => {
    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({ supabase: context.supabase, userId: context.userId, feature: "dossier", environment: data.environment });
    const db = supabaseAdmin as any;
    const { data: run, error } = await db.from("intel_scan_runs").select("*").eq("id", data.runId).single();
    if (error || !run) throw new Error("Scan run not found");
    const { data: isAdmin } = await db.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin && run.user_id && run.user_id !== context.userId) {
      throw new Error("Forbidden: scan belongs to another user");
    }

    const prompt = `Produce a 2-section executive intelligence dossier for the following OSINT scan run.

TARGET: ${run.target} (${run.target_type})
RISK: ${run.risk_score}/100  CONFIDENCE: ${run.confidence}%
SOURCES EXECUTED: ${run.source_count}
FINDINGS COUNT: ${(run.findings ?? []).length}
ENTITIES COUNT: ${(run.entities ?? []).length}

TOP FINDINGS:
${(run.findings ?? []).slice(0, 6).map((f: any) => `- [${f.severity}] ${f.title} (${f.source}): ${f.description}`).join("\n")}

Respond in JSON: {"summary": "...", "recommendations": "..."}.
Summary: 3-5 sentences executive assessment.
Recommendations: 3 bullet next steps (markdown).
LEGAL OSINT ONLY.`;

    const { callGrok } = await import("@/lib/intel/grok.server");
    const res = await callGrok(
      [
        { role: "system", content: "You are GROK-AGENT. Output ONLY valid JSON. Legal OSINT methodology only. Use Live Search when helpful." },
        { role: "user", content: prompt },
      ],
      { responseJson: true, liveSearch: false },
    );
    const content = res.content || "{}";
    try {
      const parsed = JSON.parse(content);
      return { summary: parsed.summary ?? "", recommendations: parsed.recommendations ?? "" };
    } catch {
      return { summary: content, recommendations: "" };
    }
  });

// ============================================================
// Finding corrections — users flag bad results; scans feed these
// back into the Grok analyst prompt so it stops repeating mistakes.
// ============================================================
export const flagFinding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      scanRunId: z.string().uuid().optional(),
      target: z.string().min(1).max(240),
      source: z.string().min(1).max(160),
      findingTitle: z.string().min(1).max(400),
      reason: z.string().max(1000).optional(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const db = supabaseAdmin as any;
    const { error } = await db.from("finding_corrections").insert({
      user_id: context.userId,
      scan_run_id: data.scanRunId ?? null,
      target: data.target,
      source: data.source,
      finding_title: data.findingTitle,
      reason: data.reason ?? null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listMyCorrections = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ target: z.string().min(1).max(240) }).parse(input))
  .handler(async ({ data, context }) => {
    const db = supabaseAdmin as any;
    const { data: rows } = await db
      .from("finding_corrections")
      .select("source,finding_title,reason,created_at")
      .eq("user_id", context.userId)
      .eq("target", data.target)
      .order("created_at", { ascending: false })
      .limit(20);
    return (rows ?? []) as Array<{ source: string; finding_title: string; reason: string | null; created_at: string }>;
  });

// ============================================================
// Audience segment — set on signup, drives copy/defaults later.
// ============================================================
const AUDIENCE_VALUES = [
  "investigator", "lawyer", "individual", "startup", "vc",
  "enterprise", "journalist", "compliance", "other",
  // legacy value kept accepted for older clients
  "investor",
] as const;

export const saveAudience = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      audience: z.enum(AUDIENCE_VALUES),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const db = supabaseAdmin as any;
    const { error } = await db.from("profiles")
      .update({ audience: data.audience })
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getMyAudience = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ audience: string | null }> => {
    const db = supabaseAdmin as any;
    const { data } = await db
      .from("profiles")
      .select("audience")
      .eq("user_id", context.userId)
      .maybeSingle();
    return { audience: (data?.audience as string | null) ?? null };
  });

// ============================================================
// Business intelligence — competitors, chairmen and key people.
// ============================================================
export type BusinessPerson = {
  name: string;
  role: string;
  background: string;
  risk_notes: string;
  sources: string[];
};

export type BusinessCompetitor = {
  name: string;
  domain: string;
  positioning: string;
  key_people: string[];
  relative_strength: string;
  sources: string[];
};

export type BusinessIntel = {
  company: {
    legal_name: string;
    summary: string;
    hq: string;
    founded: string;
    industry: string;
    size: string;
    ownership: string;
  };
  key_people: BusinessPerson[];
  competitors: BusinessCompetitor[];
  risk_flags: Array<{ label: string; severity: "low" | "medium" | "high" | "critical"; detail: string }>;
  opportunities: string[];
  citations: string[];
  generatedAt: string;
};

export const runBusinessIntel = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      company: z.string().min(2).max(200),
      focus: z.enum(["overview", "competitors", "people", "ownership"]).default("overview"),
      environment: z.enum(["sandbox", "live"]).default("live"),
      industry: z.string().trim().max(120).optional(),
      geography: z.string().trim().max(120).optional(),
      ownership: z.string().trim().max(60).optional(),
      roles: z.array(z.string().trim().max(40)).max(12).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<BusinessIntel> => {
    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({ supabase: context.supabase, userId: context.userId, feature: "ai", environment: data.environment });
    const { callGrok } = await import("@/lib/intel/grok.server");

    const focusLine = {
      overview: "Give a balanced company profile with leadership and competitors.",
      competitors: "Emphasise the competitive set: direct rivals, their leadership and how they compare.",
      people: "Emphasise the people: chairman, board, C-suite, founders, notable shareholders and their track record.",
      ownership: "Emphasise ownership and control: parent entities, beneficial owners, major shareholders, officers.",
    }[data.focus];

    const filters: string[] = [];
    if (data.industry) filters.push(`INDUSTRY / SECTOR: restrict the company and the competitive set to "${data.industry}".`);
    if (data.geography) filters.push(`GEOGRAPHY: restrict to entities and operations in "${data.geography}". Ignore same-named entities elsewhere.`);
    if (data.ownership && data.ownership !== "any") filters.push(`OWNERSHIP STRUCTURE: only consider entities whose structure is "${data.ownership}"; always resolve the ultimate beneficial owners (UBO) chain where public.`);
    if (data.roles?.length) filters.push(`BOARD / C-SUITE ROLES: return only people holding these roles — ${data.roles.join(", ")}. Set "role" to the exact matching role. Omit anyone outside this list.`);
    const filterBlock = filters.length ? `\nHARD FILTERS (must be respected):\n- ${filters.join("\n- ")}\n` : "";

    const res = await callGrok(
      [
        {
          role: "system",
          content:
            "You are CARIO BUSINESS-INTEL, a corporate research analyst with live web search. " +
            "Use only legal, publicly accessible sources: registries, filings, company sites, reputable press. " +
            "Never invent people or numbers — leave a field empty rather than guessing. " +
            "Return STRICT JSON only, no prose outside JSON.",
        },
        {
          role: "user",
          content: `COMPANY: ${data.company}
FOCUS: ${focusLine}
${filterBlock}

Research this company live and return JSON with exactly this shape:
{
  "company": {"legal_name":"","summary":"3-5 sentences","hq":"","founded":"","industry":"","size":"","ownership":""},
  "key_people": [{"name":"","role":"chairman|CEO|CFO|board member|founder|owner","background":"","risk_notes":"","sources":["url"]}],
  "competitors": [{"name":"","domain":"","positioning":"","key_people":["name — role"],"relative_strength":"","sources":["url"]}],
  "risk_flags": [{"label":"","severity":"low|medium|high|critical","detail":""}],
  "opportunities": ["short actionable observations"]
}
Include up to 10 key people and up to 8 competitors.`,
        },
      ],
      { liveSearch: true, searchTools: ["web_search", "x_search"] },
    );

    let parsed: any = {};
    try { parsed = JSON.parse(res.content); } catch {
      const m = res.content.match(/\{[\s\S]*\}/);
      if (m) { try { parsed = JSON.parse(m[0]); } catch { /* ignore */ } }
    }
    const c = parsed?.company ?? {};
    return {
      company: {
        legal_name: String(c.legal_name ?? data.company),
        summary: String(c.summary ?? ""),
        hq: String(c.hq ?? ""),
        founded: String(c.founded ?? ""),
        industry: String(c.industry ?? ""),
        size: String(c.size ?? ""),
        ownership: String(c.ownership ?? ""),
      },
      key_people: Array.isArray(parsed?.key_people) ? parsed.key_people.slice(0, 12).map((p: any) => ({
        name: String(p?.name ?? ""),
        role: String(p?.role ?? ""),
        background: String(p?.background ?? ""),
        risk_notes: String(p?.risk_notes ?? ""),
        sources: Array.isArray(p?.sources) ? p.sources.map(String).slice(0, 5) : [],
      })).filter((p: BusinessPerson) => p.name) : [],
      competitors: Array.isArray(parsed?.competitors) ? parsed.competitors.slice(0, 10).map((k: any) => ({
        name: String(k?.name ?? ""),
        domain: String(k?.domain ?? ""),
        positioning: String(k?.positioning ?? ""),
        key_people: Array.isArray(k?.key_people) ? k.key_people.map(String).slice(0, 6) : [],
        relative_strength: String(k?.relative_strength ?? ""),
        sources: Array.isArray(k?.sources) ? k.sources.map(String).slice(0, 5) : [],
      })).filter((k: BusinessCompetitor) => k.name) : [],
      risk_flags: Array.isArray(parsed?.risk_flags) ? parsed.risk_flags.slice(0, 12).map((r: any) => ({
        label: String(r?.label ?? ""),
        severity: (["low", "medium", "high", "critical"].includes(r?.severity) ? r.severity : "low") as Severity,
        detail: String(r?.detail ?? ""),
      })).filter((r: { label: string }) => r.label) : [],
      opportunities: Array.isArray(parsed?.opportunities) ? parsed.opportunities.map(String).slice(0, 8) : [],
      citations: res.citations.slice(0, 20),
      generatedAt: new Date().toISOString(),
    };
  });
// ============================================================
// 1e. AI Contradiction Detection
// ============================================================
export type Contradiction = {
  claimA: { statement: string; source: string; date?: string };
  claimB: { statement: string; source: string; date?: string };
  field: string;
  severity: "low" | "medium" | "high";
  confidence: number;
  explanation: string;
  resolution: string;
  likelyCorrect: "a" | "b" | "unknown";
};
export type ContradictionReport = {
  contradictions: Contradiction[];
  summary: string;
  citations: string[];
  generatedAt: string;
};

export const detectContradictions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      scanRunId: z.string().uuid(),
      environment: z.enum(["sandbox", "live"]).default("live"),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<ContradictionReport> => {
    const db = supabaseAdmin as any;
    await assertScanAccess(db, data.scanRunId, context.userId);

    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({
      supabase: context.supabase,
      userId: context.userId,
      feature: "ai",
      environment: data.environment,
    });

    const { data: run } = await db
      .from("intel_scan_runs")
      .select("target, target_type, findings, entities, timeline, raw")
      .eq("id", data.scanRunId)
      .single();

    const collectors: any[] = Array.isArray(run?.raw?.collectors) ? run.raw.collectors : [];
    const findingsBlock = (run?.findings ?? []).slice(0, 30).map(
      (f: any) => `- [${f.severity}] ${f.title} (${f.source}): ${String(f.description ?? "").slice(0, 300)}`,
    ).join("\n");
    const collectorsBlock = collectors.slice(0, 40).map(
      (c: any) => `- ${c.source} (${c.status}): ${String(c.summary ?? "").slice(0, 220)}`,
    ).join("\n");
    const timelineBlock = Array.isArray(run?.timeline)
      ? [...run.timeline]
          .filter((t: any) => t?.kind !== "run" && t?.category !== "run")
          .sort((a: any, b: any) => String(a?.ts).localeCompare(String(b?.ts)))
          .slice(0, 40)
          .map((t: any) => `- ${String(t?.ts ?? "").slice(0, 10)} ${t?.title} (${t?.source})`)
          .join("\n")
      : "";

    const system = {
      role: "system" as const,
      content: [
        "You are CARIO-CONTRADICT, an OSINT verification specialist whose only job is to find CONFLICTING information about a target.",
        "A contradiction is two claims that cannot both be true at the same time: e.g. LinkedIn says 'CEO of Acme' while the official company registry shows the person resigned as director 3 years ago; or a WHOIS country differs from a registry jurisdiction; or a status is 'active' in one source and 'dissolved' in another; or dates conflict.",
        "Use live web/X search to pull the current public state of the target from primary sources (official registries, court records, sanctions lists, filings) and compare it against platform/self-reported claims (LinkedIn, personal sites, press, social bios).",
        "Rank source authority: government registry > regulated filing > curated dataset > platform profile > aggregator > forum. Self-reported claims are the usual loser when they conflict with a registry.",
        "Only report REAL conflicts. Do NOT invent claims, and never report two compatible facts as a contradiction. If nothing conflicts, return an empty array.",
        "Return STRICT JSON only. No prose outside JSON.",
      ].join(" "),
    };
    const user = {
      role: "user" as const,
      content: `TARGET: ${run?.target} (${run?.target_type})

FINDINGS:
${findingsBlock || "(none)"}

COLLECTOR RESULTS:
${collectorsBlock || "(none)"}

CHRONOLOGY:
${timelineBlock || "(no dated evidence)"}

TASK: find conflicting information about this target across the evidence above plus live search. Return JSON:
{
  "summary": "1-3 sentences on the state of agreement across sources",
  "contradictions": [{
    "field": "short label e.g. current role, company status, jurisdiction, date of birth",
    "claim_a": {"statement":"what source A asserts","source":"source name or url","date":"YYYY-MM-DD or empty"},
    "claim_b": {"statement":"what source B asserts","source":"source name or url","date":"YYYY-MM-DD or empty"},
    "severity": "low|medium|high",
    "confidence": 0-100,
    "explanation": "why these cannot both be true",
    "likely_correct": "a|b|unknown",
    "resolution": "the concrete check that would settle it"
  }]
}
Return 0-8 contradictions, highest severity first.`,
    };

    const { callGrok } = await import("@/lib/intel/grok.server");
    const res = await callGrok([system, user], { liveSearch: true });
    let parsed: any = {};
    try { parsed = JSON.parse(res.content); } catch {
      const m = res.content.match(/\{[\s\S]*\}/);
      if (m) { try { parsed = JSON.parse(m[0]); } catch { /* ignore */ } }
    }

    const arr = Array.isArray(parsed.contradictions) ? parsed.contradictions : [];
    const claim = (c: any) => ({
      statement: String(c?.statement ?? "").slice(0, 400),
      source: String(c?.source ?? "unknown source").slice(0, 200),
      ...(c?.date ? { date: String(c.date).slice(0, 10) } : {}),
    });
    const contradictions: Contradiction[] = arr.slice(0, 8).map((c: any) => ({
      field: String(c?.field ?? "claim").slice(0, 80),
      claimA: claim(c?.claim_a),
      claimB: claim(c?.claim_b),
      severity: (["low", "medium", "high"].includes(c?.severity) ? c.severity : "medium") as Contradiction["severity"],
      confidence: clampPct(c?.confidence, 50),
      explanation: String(c?.explanation ?? "").slice(0, 800),
      resolution: String(c?.resolution ?? "").slice(0, 400),
      likelyCorrect: (["a", "b", "unknown"].includes(c?.likely_correct) ? c.likely_correct : "unknown") as Contradiction["likelyCorrect"],
    })).filter((c: Contradiction) => c.claimA.statement && c.claimB.statement);

    const rank = { high: 0, medium: 1, low: 2 } as const;
    contradictions.sort((a, b) => rank[a.severity] - rank[b.severity] || b.confidence - a.confidence);

    void writeAudit({
      scanRunId: data.scanRunId,
      userId: context.userId,
      actorEmail: (context.claims as any)?.email ?? null,
      action: "contradiction_scan",
      metadata: { contradictionScan: true, found: contradictions.length },
    });

    return {
      contradictions,
      summary: typeof parsed.summary === "string" && parsed.summary
        ? parsed.summary.slice(0, 600)
        : contradictions.length
          ? `${contradictions.length} conflicting claim set(s) detected across sources.`
          : "No contradictions detected — sources agree on the checked facts.",
      citations: (res.citations ?? []).slice(0, 20),
      generatedAt: new Date().toISOString(),
    };
  });

// ============================================================
// 1f. Organization Intelligence — one-click company dossier
// ============================================================
export type OrgNode = { id: string; name: string; kind: "parent" | "subject" | "subsidiary" | "shareholder" | "affiliate"; stake: string; jurisdiction: string; note: string };
export type OrgEdge = { from: string; to: string; label: string };
export type OrgItem = { title: string; detail: string; date: string; status: string; sources: string[] };

export type OrgDossier = {
  company: { legal_name: string; summary: string; hq: string; founded: string; industry: string; size: string; ownership: string; identifiers: string[]; domains: string[] };
  ownership_graph: { nodes: OrgNode[]; edges: OrgEdge[] };
  subsidiaries: OrgItem[];
  executives: BusinessPerson[];
  lawsuits: OrgItem[];
  patents: OrgItem[];
  sanctions: OrgItem[];
  suppliers: OrgItem[];
  technologies: OrgItem[];
  acquisitions: OrgItem[];
  hiring_trends: { summary: string; signals: OrgItem[] };
  risk_flags: Array<{ label: string; severity: Severity; detail: string }>;
  citations: string[];
  generatedAt: string;
};

const orgItems = (v: any, n = 12): OrgItem[] =>
  Array.isArray(v)
    ? v.slice(0, n).map((i: any) => ({
        title: String(i?.title ?? i?.name ?? "").slice(0, 200),
        detail: String(i?.detail ?? "").slice(0, 600),
        date: String(i?.date ?? "").slice(0, 40),
        status: String(i?.status ?? "").slice(0, 80),
        sources: Array.isArray(i?.sources) ? i.sources.map(String).slice(0, 5) : [],
      })).filter((i: OrgItem) => i.title)
    : [];

export const runOrgDossier = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      company: z.string().min(2).max(200),
      environment: z.enum(["sandbox", "live"]).default("live"),
      geography: z.string().trim().max(120).optional(),
      industry: z.string().trim().max(120).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<OrgDossier> => {
    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({ supabase: context.supabase, userId: context.userId, feature: "ai", environment: data.environment });
    const { callGrok } = await import("@/lib/intel/grok.server");

    const scope = [
      data.geography ? `Restrict to entities operating in "${data.geography}".` : "",
      data.industry ? `The company operates in "${data.industry}".` : "",
    ].filter(Boolean).join(" ");

    const res = await callGrok(
      [
        {
          role: "system",
          content:
            "You are CARIO ORG-INTEL, a corporate due-diligence analyst with live web search. " +
            "Use only legal, publicly accessible sources: company registries, regulatory filings, court dockets, patent offices, sanctions lists, procurement records, job boards, reputable press. " +
            "Never invent entities, numbers, case names or dates — omit an item rather than guessing. Cite a URL for every item you can. " +
            "Return STRICT JSON only, no prose outside JSON.",
        },
        {
          role: "user",
          content: `COMPANY: ${data.company}
${scope}

Build a full organization dossier. Return JSON with exactly this shape:
{
  "company": {"legal_name":"","summary":"4-6 sentences","hq":"","founded":"","industry":"","size":"","ownership":"","identifiers":["registration/LEI/VAT numbers"],"domains":["example.com"]},
  "ownership_graph": {
    "nodes": [{"id":"n1","name":"","kind":"parent|subject|subsidiary|shareholder|affiliate","stake":"e.g. 51%","jurisdiction":"","note":""}],
    "edges": [{"from":"n1","to":"n2","label":"owns 51%"}]
  },
  "subsidiaries": [{"title":"entity name","detail":"activity + ownership %","date":"incorporated","status":"active|dissolved","sources":["url"]}],
  "executives": [{"name":"","role":"","background":"","risk_notes":"","sources":["url"]}],
  "lawsuits": [{"title":"case name","detail":"claim + parties + outcome","date":"filed","status":"pending|settled|dismissed","sources":["url"]}],
  "patents": [{"title":"patent title","detail":"number + abstract","date":"granted/filed","status":"granted|pending","sources":["url"]}],
  "sanctions": [{"title":"listing/name","detail":"programme + reason","date":"","status":"listed|delisted|none","sources":["url"]}],
  "suppliers": [{"title":"supplier/partner","detail":"what they provide","date":"","status":"","sources":["url"]}],
  "technologies": [{"title":"technology/vendor","detail":"how it is used","date":"","status":"","sources":["url"]}],
  "acquisitions": [{"title":"target or acquirer","detail":"deal value + rationale","date":"","status":"completed|announced|terminated","sources":["url"]}],
  "hiring_trends": {"summary":"2-4 sentences on headcount direction, hot roles, locations","signals":[{"title":"e.g. 40 open roles in AI","detail":"","date":"","status":"","sources":["url"]}]},
  "risk_flags": [{"label":"","severity":"low|medium|high|critical","detail":""}]
}
Always include the subject company itself as a node with kind "subject". Use empty arrays where nothing credible is found.`,
        },
      ],
      { liveSearch: true, searchTools: ["web_search", "x_search"] },
    );

    let parsed: any = {};
    try { parsed = JSON.parse(res.content); } catch {
      const m = res.content.match(/\{[\s\S]*\}/);
      if (m) { try { parsed = JSON.parse(m[0]); } catch { /* ignore */ } }
    }

    const c = parsed?.company ?? {};
    const rawNodes = Array.isArray(parsed?.ownership_graph?.nodes) ? parsed.ownership_graph.nodes : [];
    const nodes: OrgNode[] = rawNodes.slice(0, 40).map((n: any, i: number) => ({
      id: String(n?.id ?? `n${i + 1}`),
      name: String(n?.name ?? "").slice(0, 160),
      kind: (["parent", "subject", "subsidiary", "shareholder", "affiliate"].includes(n?.kind) ? n.kind : "affiliate") as OrgNode["kind"],
      stake: String(n?.stake ?? "").slice(0, 40),
      jurisdiction: String(n?.jurisdiction ?? "").slice(0, 80),
      note: String(n?.note ?? "").slice(0, 240),
    })).filter((n: OrgNode) => n.name);
    const ids = new Set(nodes.map((n) => n.id));
    const edges: OrgEdge[] = (Array.isArray(parsed?.ownership_graph?.edges) ? parsed.ownership_graph.edges : [])
      .slice(0, 80)
      .map((e: any) => ({ from: String(e?.from ?? ""), to: String(e?.to ?? ""), label: String(e?.label ?? "").slice(0, 60) }))
      .filter((e: OrgEdge) => ids.has(e.from) && ids.has(e.to) && e.from !== e.to);

    const hs = parsed?.hiring_trends ?? {};

    return {
      company: {
        legal_name: String(c.legal_name ?? data.company),
        summary: String(c.summary ?? "").slice(0, 1600),
        hq: String(c.hq ?? ""),
        founded: String(c.founded ?? ""),
        industry: String(c.industry ?? ""),
        size: String(c.size ?? ""),
        ownership: String(c.ownership ?? ""),
        identifiers: Array.isArray(c.identifiers) ? c.identifiers.map(String).slice(0, 6) : [],
        domains: Array.isArray(c.domains) ? c.domains.map(String).slice(0, 6) : [],
      },
      ownership_graph: { nodes, edges },
      subsidiaries: orgItems(parsed?.subsidiaries, 20),
      executives: Array.isArray(parsed?.executives) ? parsed.executives.slice(0, 15).map((p: any) => ({
        name: String(p?.name ?? ""),
        role: String(p?.role ?? ""),
        background: String(p?.background ?? "").slice(0, 500),
        risk_notes: String(p?.risk_notes ?? "").slice(0, 400),
        sources: Array.isArray(p?.sources) ? p.sources.map(String).slice(0, 5) : [],
      })).filter((p: BusinessPerson) => p.name) : [],
      lawsuits: orgItems(parsed?.lawsuits, 15),
      patents: orgItems(parsed?.patents, 15),
      sanctions: orgItems(parsed?.sanctions, 10),
      suppliers: orgItems(parsed?.suppliers, 15),
      technologies: orgItems(parsed?.technologies, 20),
      acquisitions: orgItems(parsed?.acquisitions, 15),
      hiring_trends: { summary: String(hs?.summary ?? "").slice(0, 800), signals: orgItems(hs?.signals, 10) },
      risk_flags: Array.isArray(parsed?.risk_flags) ? parsed.risk_flags.slice(0, 12).map((r: any) => ({
        label: String(r?.label ?? ""),
        severity: (["low", "medium", "high", "critical"].includes(r?.severity) ? r.severity : "low") as Severity,
        detail: String(r?.detail ?? "").slice(0, 500),
      })).filter((r: { label: string }) => r.label) : [],
      citations: (res.citations ?? []).slice(0, 30),
      generatedAt: new Date().toISOString(),
    };
  });
